<script setup lang="ts">
import { useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useKeepAliveSlot } from './useKeepAliveSlot';
import { useRectTracker } from './useRectTracker';
import { useIframeKeepAlive } from './useIframeKeepAlive';
import type { IframeKind, IframePlacement, IframeSpec } from './types';

const props = withDefaults(
  defineProps<{
    kind?: IframeKind;
    placement?: IframePlacement;
    cacheKey?: string;
    src?: string;
    html?: string;
    sandbox?: string;
    maxLive?: number;
  }>(),
  {
    kind: 'url',
    placement: 'fill',
    cacheKey: undefined,
    src: undefined,
    html: undefined,
    sandbox: undefined,
    maxLive: undefined,
  },
);

const route = useRoute();
const { isActive, setRect } = useIframeKeepAlive();
const placeholder = useTemplateRef<HTMLElement>('placeholder');

function buildKey(): string {
  return `iframe:${props.cacheKey ?? props.src ?? props.html ?? route.fullPath}`;
}

function buildSpec(): IframeSpec {
  if (props.kind === 'srcdoc') {
    return {
      kind: 'srcdoc',
      params: { html: props.html ?? '', sandbox: props.sandbox },
    };
  }
  return {
    kind: 'url',
    params: { src: props.src ?? '', sandbox: props.sandbox },
  };
}

useKeepAliveSlot(
  buildKey,
  buildSpec,
  () => props.maxLive,
  () => props.placement,
);

useRectTracker(
  () => placeholder.value,
  () => props.placement === 'inline' && isActive(buildKey()),
  (rect) => setRect(buildKey(), rect),
);
</script>

<template>
  <div
    ref="placeholder"
    class="itp-keepalive-iframe"
    :class="
      placement === 'inline' ? 'itp-keepalive-iframe--inline' : 'h-100 w-100'
    "
  ></div>
</template>
